/* Gridline — product page behaviours
   - gallery: thumb click / prev-next arrows / arrow keys on focused stage
   - zoom: native <dialog> lightbox
   - quantity stepper: [data-qty-minus] / [data-qty-plus]
   - variant selection: matches selects against embedded variants JSON,
     updates id input, price, sku, availability badge, submit button, URL.
   Defensive null checks throughout so it no-ops on other pages.
*/
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    /* ---------- Money formatting ---------- */
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

    function formatMoney(cents) {
      var value = (Number(cents) / 100).toFixed(2);
      var parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, parts.join('.'))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, parts[0])
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/,/g, '.') + ',' + parts[1]);
    }

    /* ---------- Gallery ---------- */
    var stage = root.querySelector('[data-stage]');
    var stageImg = root.querySelector('[data-stage-img]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-thumb]'));
    var current = 0;

    thumbs.forEach(function (t, i) {
      if (t.classList.contains('is-active')) current = i;
    });

    function setImage(i) {
      if (!stageImg || !thumbs.length) return;
      current = ((i % thumbs.length) + thumbs.length) % thumbs.length;
      var t = thumbs[current];
      stageImg.src = t.getAttribute('data-src') || stageImg.src;
      stageImg.srcset = t.getAttribute('data-srcset') || '';
      stageImg.alt = t.getAttribute('data-alt') || '';
      if (t.getAttribute('data-width')) stageImg.setAttribute('width', t.getAttribute('data-width'));
      if (t.getAttribute('data-height')) stageImg.setAttribute('height', t.getAttribute('data-height'));
      stageImg.setAttribute('data-zoom-src', t.getAttribute('data-zoom-src') || t.getAttribute('data-src') || '');
      thumbs.forEach(function (x, n) {
        x.classList.toggle('is-active', n === current);
        if (n === current) x.setAttribute('aria-current', 'true');
        else x.removeAttribute('aria-current');
      });
      if (t.scrollIntoView) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { setImage(i); });
    });

    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { setImage(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setImage(current + 1); });

    if (stage) {
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); setImage(current - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); setImage(current + 1); }
      });
    }

    /* ---------- Zoom lightbox ---------- */
    var zoomBtn = root.querySelector('[data-zoom-open]');
    var zoomDialog = root.querySelector('[data-zoom-dialog]');
    var zoomImg = zoomDialog ? zoomDialog.querySelector('[data-zoom-img]') : null;

    if (zoomBtn && zoomDialog && zoomImg && typeof zoomDialog.showModal === 'function') {
      zoomBtn.addEventListener('click', function () {
        var src = stageImg ? (stageImg.getAttribute('data-zoom-src') || stageImg.src) : '';
        if (!src) return;
        zoomImg.src = src;
        if (stageImg && stageImg.alt) zoomImg.alt = stageImg.alt;
        zoomDialog.showModal();
      });
      zoomDialog.addEventListener('click', function (e) {
        if (e.target.closest('[data-zoom-close]')) { zoomDialog.close(); return; }
        /* click anywhere except the image itself closes (backdrop-style) */
        if (!e.target.closest('img')) zoomDialog.close();
      });
    }

    /* ---------- Touch swipe on gallery stage ---------- */
    if (stage && thumbs.length > 1) {
      var touchX = 0, touchY = 0;
      stage.addEventListener('touchstart', function (e) {
        touchX = e.changedTouches[0].clientX;
        touchY = e.changedTouches[0].clientY;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - touchX;
        var dy = e.changedTouches[0].clientY - touchY;
        if (Math.abs(dx) > 40 && Math.abs(dy) < Math.abs(dx)) {
          setImage(dx < 0 ? current + 1 : current - 1);
        }
      }, { passive: true });
    }

    /* ---------- Zoom pan/1x helpers ---------- */
    var zoomBody = zoomDialog ? zoomDialog.querySelector('.dc-product__zoom-body') : null;
    function centerZoomScroll() {
      if (!zoomBody) return;
      zoomBody.scrollLeft = (zoomBody.scrollWidth - zoomBody.clientWidth) / 2;
      zoomBody.scrollTop = (zoomBody.scrollHeight - zoomBody.clientHeight) / 2;
    }
    if (zoomDialog && zoomImg) {
      zoomImg.addEventListener('load', function () { requestAnimationFrame(centerZoomScroll); });
      zoomImg.addEventListener('click', function () {
        if (window.matchMedia('(min-width: 990px)').matches && zoomBody) {
          zoomBody.classList.toggle('is-1x');
          requestAnimationFrame(centerZoomScroll);
        }
      });
      zoomDialog.addEventListener('close', function () {
        if (zoomBody) zoomBody.classList.remove('is-1x');
      });
    }

    /* ---------- Mobile sticky add-to-cart bar ---------- */
    var stickyBar = root.querySelector('[data-sticky-atc]');
    var buyForm = root.querySelector('.dc-product__form');
    var realBtn = buyForm ? buyForm.querySelector('[data-submit-btn]') : null;
    if (stickyBar && buyForm && realBtn) {
      var stickyBtn = stickyBar.querySelector('[data-sticky-atc-btn]');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', function () { realBtn.click(); });
        var mirror = new MutationObserver(function () {
          stickyBtn.disabled = realBtn.disabled;
          stickyBtn.textContent = realBtn.textContent;
        });
        mirror.observe(realBtn, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      }
      var stickyTick = false;
      function updateSticky() {
        stickyTick = false;
        if (buyForm.getBoundingClientRect().bottom < 0) {
          stickyBar.removeAttribute('hidden');
          stickyBar.setAttribute('aria-hidden', 'false');
          requestAnimationFrame(function () { stickyBar.classList.add('is-visible'); });
        } else {
          stickyBar.classList.remove('is-visible');
          stickyBar.setAttribute('aria-hidden', 'true');
        }
      }
      window.addEventListener('scroll', function () {
        if (!stickyTick) { stickyTick = true; requestAnimationFrame(updateSticky); }
      }, { passive: true });
      updateSticky();
    }

    /* ---------- Description clamp (mobile) ---------- */
    if (window.matchMedia('(max-width: 749px)').matches) {
      var rte = root.querySelector('.dc-product__accordions details .dc-product__rte');
      if (rte && rte.scrollHeight > 430) {
        rte.classList.add('dc-rte-clamp');
        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'dc-rte-toggle';
        toggle.textContent = 'Read more';
        toggle.setAttribute('aria-expanded', 'false');
        rte.parentNode.insertBefore(toggle, rte.nextSibling);
        toggle.addEventListener('click', function () {
          var expanded = rte.classList.toggle('is-expanded');
          toggle.textContent = expanded ? 'Read less' : 'Read more';
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
      }
    }

    /* ---------- Quantity steppers ---------- */
    root.querySelectorAll('[data-qty]').forEach(function (wrap) {
      var input = wrap.querySelector('input[name="quantity"]');
      if (!input) return;
      function step(delta) {
        var v = parseInt(input.value, 10);
        if (isNaN(v)) v = 1;
        var min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, v + delta);
      }
      var minus = wrap.querySelector('[data-qty-minus]');
      var plus = wrap.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', function () { step(-1); });
      if (plus) plus.addEventListener('click', function () { step(1); });
    });

    /* ---------- Variant selection ---------- */
    var variantsEl = root.querySelector('script[type="application/json"][data-variants]');
    var selects = Array.prototype.slice.call(root.querySelectorAll('[data-option-select]'));
    if (!variantsEl || !selects.length) return;

    var variants = [];
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants)) return;

    var priceWrap = root.querySelector('[data-price-wrap]');
    var availEl = root.querySelector('[data-availability]');
    var skuEl = root.querySelector('[data-sku]');
    var skuWrap = root.querySelector('[data-sku-wrap]');

    function currentVariant() {
      var opts = selects.map(function (s) { return s.value; });
      for (var i = 0; i < variants.length; i++) {
        var match = true;
        for (var j = 0; j < opts.length; j++) {
          if (variants[i].options[j] !== opts[j]) { match = false; break; }
        }
        if (match) return variants[i];
      }
      return null;
    }

    function updatePrice(variant) {
      if (!priceWrap) return;
      var priceRoot = priceWrap.querySelector('.dc-price') || priceWrap;
      var amountEl = priceWrap.querySelector('[data-price]') || priceWrap.querySelector('.dc-price__amount');
      var compareEl = priceWrap.querySelector('[data-compare-price]') || priceWrap.querySelector('.dc-price__compare');
      if (amountEl) amountEl.textContent = formatMoney(variant.price);
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (onSale) {
        if (!compareEl && amountEl && amountEl.parentNode) {
          compareEl = document.createElement('s');
          compareEl.className = 'dc-price__compare';
          compareEl.setAttribute('data-compare-price', '');
          amountEl.parentNode.insertBefore(compareEl, amountEl.nextSibling);
        }
        if (compareEl) {
          compareEl.hidden = false;
          compareEl.textContent = formatMoney(variant.compare_at_price);
        }
      } else if (compareEl) {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
      priceRoot.classList.toggle('dc-price--sale', !!onSale);
      var saleBadge = priceWrap.querySelector('.dc-badge--sale');
      if (saleBadge) saleBadge.hidden = !onSale;
    }

    function updateAvailability(variant) {
      if (!availEl) return;
      var available = !!(variant && variant.available);
      availEl.classList.remove('dc-badge--low');
      availEl.classList.toggle('dc-badge--stock', available);
      availEl.classList.toggle('dc-badge--soldout', !available);
      availEl.textContent = available
        ? (availEl.getAttribute('data-label-in') || 'In stock')
        : (availEl.getAttribute('data-label-out') || 'Out of stock');
    }

    function updateBis(variant) {
      var bis = root.querySelector('[data-bis]');
      if (!bis) return;
      var available = !!(variant && variant.available);
      bis.hidden = available;
      var tags = bis.querySelector('[data-bis-tags]');
      if (tags && variant) {
        tags.value = 'back-in-stock,bis-' + (variant.sku || variant.id);
      }
    }

    function updateShipEta(variant) {
      var block = root.querySelector('[data-ship-block]');
      if (!block) return;
      var body = block.querySelector('[data-ship-body]') || block;
      var note = block.querySelector('[data-ship-note]');
      var available = !!(variant && variant.available);

      var main = block.querySelector('[data-ship-main]');
      if (main) main.hidden = !available;

      var eta = block.querySelector('[data-ship-eta]');
      var unavail = block.querySelector('[data-ship-unavailable]');

      if (available) {
        if (unavail) unavail.parentNode.removeChild(unavail);
        if (!eta) {
          eta = document.createElement('p');
          eta.className = 'dc-product__ship-eta';
          eta.setAttribute('data-ship-eta', '');
          var range = block.getAttribute('data-eta-range') || '';
          eta.innerHTML = 'Order today — estimated delivery <strong></strong> with standard shipping';
          eta.querySelector('strong').textContent = range;
          if (note) body.insertBefore(eta, note); else body.appendChild(eta);
        }
        eta.hidden = false;
      } else {
        if (eta) eta.parentNode.removeChild(eta);
        if (!unavail) {
          unavail = document.createElement('p');
          unavail.className = 'dc-product__ship-unavailable';
          unavail.setAttribute('data-ship-unavailable', '');
          unavail.textContent = 'Currently unavailable';
          if (note) body.insertBefore(unavail, note); else body.appendChild(unavail);
        }
        unavail.hidden = false;
      }
    }

    function updateButtons(variant) {
      updateBis(variant);
      root.querySelectorAll('[data-submit-btn]').forEach(function (btn) {
        if (!variant) {
          btn.disabled = true;
          btn.textContent = btn.getAttribute('data-label-unavailable') || 'Unavailable';
        } else if (!variant.available) {
          btn.disabled = true;
          btn.textContent = btn.getAttribute('data-label-soldout') || 'Sold Out';
        } else {
          btn.disabled = false;
          btn.textContent = btn.getAttribute('data-label-add') || 'Add to Cart';
        }
      });
    }

    function onVariantChange() {
      var variant = currentVariant();
      updateButtons(variant);
      updateAvailability(variant);
      updateShipEta(variant);
      if (!variant) return;

      root.querySelectorAll('input[name="id"]').forEach(function (input) {
        input.value = variant.id;
      });

      updatePrice(variant);

      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        if (skuWrap) skuWrap.hidden = !variant.sku;
      }

      if (variant.featured_image && thumbs.length) {
        for (var i = 0; i < thumbs.length; i++) {
          if (thumbs[i].getAttribute('data-image-id') === String(variant.featured_image.id)) {
            setImage(i);
            break;
          }
        }
      }

      if (window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* older browsers */ }
      }
    }

    selects.forEach(function (s) { s.addEventListener('change', onVariantChange); });
  });
})();

